import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpService } from 'src/app/service/http/http.service';

interface LogEntry {
  timestampNs: string;
  container: string;
  level: string | null;
  message: string;
}

const LOG_LEVELS = ['INFO', 'WARN', 'ERROR', 'DEBUG'] as const;
type LogLevel = (typeof LOG_LEVELS)[number];

const LOG_RETENTION_DAYS = 7;
const LOG_LINE_HEIGHT_PX = 24;
const MAX_LOG_ENTRIES = 3000;
const LOG_AUTOFOLLOW_THRESHOLD_PX = 40;

@Component({
  selector: 'app-suite-logs-panel',
  templateUrl: './suite-logs-panel.component.html',
  styleUrls: ['./suite-logs-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuiteLogsPanelComponent implements OnChanges, OnDestroy {
  @Input() environmentName: string | null = null;
  @Input() testSuitId: string | null = null;
  /** Data de início/finalização da execução — usada pra checar retenção de 7 dias do Loki. */
  @Input() executedAt: string | Date | null = null;

  readonly logLevels = LOG_LEVELS;
  readonly logLineHeight = LOG_LINE_HEIGHT_PX;
  readonly retentionDays = LOG_RETENTION_DAYS;

  entries: LogEntry[] = [];
  containers: string[] = [];
  container = '';
  search = '';
  hiddenLevels = new Set<LogLevel>();
  loading = false;
  loaded = false;
  available = true;
  expiredByRetention = false;

  private readonly destroy$ = new Subject<void>();
  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly httpService: HttpService,
    private readonly toastr: ToastrService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.environmentName || changes.testSuitId || changes.executedAt) {
      this.resetAndLoad();
    }
  }

  ngOnDestroy(): void {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetAndLoad(): void {
    this.entries = [];
    this.containers = [];
    this.container = '';
    this.search = '';
    this.hiddenLevels = new Set();
    this.loading = false;
    this.loaded = false;
    this.available = true;
    this.expiredByRetention = this.isPastRetention();

    if (!this.expiredByRetention) this.loadLogs();
    else this.cdr.markForCheck();
  }

  private isPastRetention(): boolean {
    if (!this.executedAt) return false;
    const executed = new Date(this.executedAt);
    if (Number.isNaN(executed.getTime())) return false;

    const cutoff = Date.now() - LOG_RETENTION_DAYS * 24 * 60 * 60 * 1000;
    return executed.getTime() < cutoff;
  }

  private loadLogs(incremental = false): void {
    if (!this.testSuitId) return;
    if (this.expiredByRetention) return;
    if (incremental && (this.loading || !this.loaded)) return;

    const sinceNs = incremental ? this.entries[this.entries.length - 1]?.timestampNs : undefined;
    this.loading = true;
    this.cdr.markForCheck();

    const url = this.buildLogsUrl(sinceNs);
    this.httpService
      .genericGetNoProject<{ entries: LogEntry[]; containers: string[]; available: boolean }>(url)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.available = res.available;
          const merged = incremental ? [...this.entries, ...res.entries] : res.entries;
          this.entries =
            merged.length > MAX_LOG_ENTRIES ? merged.slice(merged.length - MAX_LOG_ENTRIES) : merged;
          if (res.containers.length) {
            this.containers = Array.from(new Set([...this.containers, ...res.containers])).sort();
          }
          this.loaded = true;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
          if (!incremental) {
            this.toastr.show('Erro ao carregar logs', 'Erro', { timeOut: 5000, messageClass: 'error' });
          }
        },
      });
  }

  private buildLogsUrl(sinceNs?: string, path = 'logs'): string {
    // O path :environment não é usado pelo backend (só o testSuitId importa no Loki), mas a
    // rota exige o segmento — fallback `_` cobre suítes legadas sem environmentId.
    const env = this.environmentName || '_';
    let url = `environments/${env}/${path}?testSuitId=${this.testSuitId}`;
    if (this.container) url += `&container=${encodeURIComponent(this.container)}`;
    if (this.search.trim()) url += `&search=${encodeURIComponent(this.search.trim())}`;
    if (sinceNs) url += `&sinceNs=${encodeURIComponent(sinceNs)}`;
    return url;
  }

  setLogContainer(container: string): void {
    if (this.container === container) return;
    this.container = container;
    this.entries = [];
    this.loaded = false;
    this.loadLogs();
  }

  onLogSearchInput(value: string): void {
    this.search = value;
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.entries = [];
      this.loaded = false;
      this.loadLogs();
    }, 350);
  }

  toggleLogLevel(level: LogLevel): void {
    if (this.hiddenLevels.has(level)) this.hiddenLevels.delete(level);
    else this.hiddenLevels.add(level);
    // Set mutation não dispara OnPush sozinho
    this.hiddenLevels = new Set(this.hiddenLevels);
    this.cdr.markForCheck();
  }

  isLogLevelHidden(level: LogLevel): boolean {
    return this.hiddenLevels.has(level);
  }

  get visibleEntries(): LogEntry[] {
    if (!this.hiddenLevels.size) return this.entries;
    return this.entries.filter(entry => !entry.level || !this.hiddenLevels.has(entry.level as LogLevel));
  }

  formatLogTime(timestampNs: string): string {
    const ms = Number(timestampNs) / 1000000;
    return new Date(ms).toLocaleTimeString('pt-BR', { hour12: false });
  }

  trackByLogEntry(index: number, entry: LogEntry): string {
    return `${entry.timestampNs}-${entry.container}-${index}`;
  }

  onLogsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom > LOG_AUTOFOLLOW_THRESHOLD_PX) return;
    if (!this.loading) this.loadLogs(true);
  }

  downloadLogs(): void {
    if (!this.testSuitId || this.expiredByRetention) return;

    const url = this.buildLogsUrl(undefined, 'logs/download');
    this.httpService.genericDownloadNoProject(url).subscribe({
      next: (blob: Blob) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = `logs-${this.environmentName || 'suite'}-${this.testSuitId}.log`;
        a.click();
        URL.revokeObjectURL(objectUrl);
      },
      error: () => {
        this.toastr.show('Erro ao baixar logs', 'Erro', { timeOut: 5000, messageClass: 'error' });
      },
    });
  }
}
